"use client";

import { AllFeaturesEnabled, ChatClient, ChatClientProvider } from '@ably/chat';
import * as Ably from 'ably';
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createClient } from '@/utils/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Users } from "lucide-react";
import Swal from 'sweetalert2';

interface Props {
  clientId: string;
  roomId: string;
}

interface ProfileData {
  nickname: string | null;
  avatar: string | null;
}

// 유틸: 클라이언트 IP 가져오기
async function fetchClientIp() {
  try {
    const res = await fetch('https://api.ip.pe.kr/');
    if (!res.ok) return '';
    const data = await res.text();
    return data || '';
  } catch {
    return '';
  }
}

export const ChatProviders = ({ clientId, roomId }: Props) => {
  const supabase = createClient();
  const [profileData, setProfileData] = useState<ProfileData>({ nickname: null, avatar: null });
  const [isReady, setIsReady] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [messages, setMessages] = useState<Ably.Message[]>([]);
  const [members, setMembers] = useState<Ably.PresenceMessage[]>([]);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; msg: any } | null>(null);

  const spamKeywords = ['spam', 'advertisement']; // 도배 키워드
  const negativeKeywords = ['badword1', 'badword2']; // 부정 키워드
  const [lastMessages, setLastMessages] = useState<{ timestamp: number }[]>([]);
  const [isChatRestricted, setIsChatRestricted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const checkForSpam = (message: string) => {
    const lowerCaseMessage = message.toLowerCase();
    const isSpamKeyword = spamKeywords.some(keyword => lowerCaseMessage.includes(keyword));
    const isNegativeKeyword = negativeKeywords.some(keyword => lowerCaseMessage.includes(keyword));
    const isRepeating = /([\w\W])\1{20,}/.test(lowerCaseMessage); // 20회 이상 반복되는 문자
    const isTooLong = lowerCaseMessage.length > 100;

    return isSpamKeyword || isNegativeKeyword || isRepeating || isTooLong;
  };

  // 프로필 정보 가져오기
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!clientId || clientId.startsWith('손님_')) {
        setProfileData({ 
          nickname: clientId || '익명',
          avatar: null 
        });
        setIsReady(true);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('nickname, avatar_url')
        .eq('user_id', clientId)
        .single();

      if (!error && data) {
        setProfileData({
          nickname: data.nickname,
          avatar: data.avatar_url
        });
      } else {
        setProfileData({
          nickname: '익명',
          avatar: null
        });
      }
      setIsReady(true);
    };

    fetchProfileData();
  }, [clientId, supabase]);

  const ablyClient = useMemo(() => {
    if (typeof window === 'undefined' || !clientId || !isReady) return null;
    
    return new Ably.Realtime({
      key: process.env.NEXT_PUBLIC_ABLY_API_KEY,
      authCallback: async (tokenParams, callback) => {
        try {
          const response = await fetch(`/api/ably-token?clientId=${encodeURIComponent(clientId)}`);
          if (!response.ok) {
            throw new Error(`Failed to fetch Ably token: ${response.statusText}`);
          }
          const tokenRequest = await response.json();
          callback(null, tokenRequest);
        } catch (error) {
          callback(error instanceof Error ? error.message : "Unknown auth error", null);
        }
      },
      clientId,
    });
  }, [clientId, isReady]);

  const chatClient = useMemo(() => {
    if (!ablyClient) return null;
    return new ChatClient(ablyClient, {});
  }, [ablyClient]);

  useEffect(() => {
    if (!ablyClient || !roomId || !profileData.nickname) return;

    const channel = ablyClient.channels.get(roomId);

    const setupPresence = async () => {
      try {
        await channel.presence.enter({ 
          profileData: {
            nickname: profileData.nickname,
            avatar: profileData.avatar
          }
        });
        console.log("Entered presence with profile data:", profileData);
      } catch (error) {
         console.error("Error entering presence:", error);
      }
    };

    setupPresence();

    const messageListener = (message: Ably.Message) => {
      console.log('Received raw Ably message:', message);
      setMessages(prev => prev.find(m => m.id === message.id) ? prev : [...prev, message]);
    };

    const presenceListener = (presenceMessage: Ably.PresenceMessage) => {
      console.log('Received presence message:', presenceMessage);
      if (!['enter', 'leave', 'update'].includes(presenceMessage.action)) return;

      setMembers(prev => {
        const existingMemberIndex = prev.findIndex(m => m.clientId === presenceMessage.clientId);

        if (presenceMessage.action === 'leave') {
          console.log(`${presenceMessage.clientId} left`);
          return prev.filter(m => m.clientId !== presenceMessage.clientId);
        } else {
           if (existingMemberIndex !== -1) {
             console.log(`${presenceMessage.clientId} updated presence`);
             const updatedMembers = [...prev];
             updatedMembers[existingMemberIndex] = presenceMessage;
             return updatedMembers;
           } else {
             console.log(`${presenceMessage.clientId} entered`);
             return [...prev, presenceMessage];
           }
        }
      });
    };

    const fetchInitialMembers = async () => {
      try {
        const initialMembers: Ably.PresenceMessage[] = await channel.presence.get();
        console.log('Initial presence members:', initialMembers);
        setMembers(initialMembers);
      } catch (err) {
        console.error("Error fetching initial presence members:", err);
      }
    };

    fetchInitialMembers();

    channel.subscribe('message', messageListener);
    channel.presence.subscribe(['enter', 'leave', 'update'], presenceListener);

    console.log(`Subscribed to messages and presence on channel: ${channel.name}`);

    return () => {
      console.log(`Unsubscribing from messages and presence on channel: ${channel.name}`);
      channel.unsubscribe('message', messageListener);
      channel.presence.unsubscribe(presenceListener);
      channel.presence.leave().catch(err => console.error("Error leaving presence on unsubscribe:", err));
    };
  }, [ablyClient, roomId, profileData]);

  // 기존 채팅 메시지 불러오기
  useEffect(() => {
    const fetchChatMessages = async () => {
      if (!roomId) return;

      const { data: messagesData, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('room_id', roomId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('기존 채팅 메시지 불러오기 오류:', error);
        return;
      }

      if (messagesData) {
        setMessages(messagesData.map((msg: any) => ({
          id: msg.id,
          clientId: msg.user_id,
          data: { text: msg.content, ip:msg.ip, profileData: { nickname: msg.nickname, avatar: msg.avatar_url } },
          timestamp: new Date(msg.created_at).getTime(),
        })));
      }
    };

    fetchChatMessages();
  }, [roomId, supabase]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleReport = useCallback(async (msg: any) => {
    const result = await Swal.fire({
      title: '신고하시겠습니까?',
      text: '이 채팅을 신고하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '신고',
      cancelButtonText: '취소',
    });
    if (result.isConfirmed) {
      // 신고자 IP
      const reporterIp = await fetchClientIp();
      // 피신고자 IP (chat_messages에 저장된 ip)
      let reportedIp = msg.data?.ip || '';
      if (!reportedIp || reportedIp === 'null') {
        Swal.fire('아이피가 기록되지 않은 회원입니다.', '', 'info');
        return;
      }
      // 피신고자 전체 채팅 내역
      let reportedHistory = [];
      try {
        const res = await fetch(`/api/chat-history?user_id=${msg.clientId}&room_id=${roomId}`);
        if (res.ok) reportedHistory = await res.json();
      } catch {}
      await fetch('/api/report-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reporterIp,
          reportedIp,
          roomId,
          reportedMessage: msg,
          reportedHistory,
        }),
      });
      Swal.fire('신고 완료', '신고가 접수되었습니다.', 'success');
    }
  }, [roomId]);

  const handleDelete = async (msg: any) => {
    const isGuest = msg.clientId?.startsWith('손님_');
    if (msg.clientId === clientId || isGuest) {
      const { error } = await supabase.from('chat_messages').delete().eq('id', msg.id);
      if (!error) {
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        Swal.fire('삭제 완료', '메시지가 삭제되었습니다.', 'success');
      } else {
        Swal.fire('삭제 실패', '메시지 삭제 중 오류가 발생했습니다.', 'error');
      }
    } else {
      Swal.fire('삭제 불가', '본인 또는 손님 채팅만 삭제할 수 있습니다.', 'info');
    }
    setContextMenu(null);
  };

  const handleSend = async () => {
    if (isChatRestricted) {
      console.warn('채팅 제한 중입니다. 잠시 후 다시 시도하세요.');
      return;
    }

    if (!messageInput.trim() || !profileData.nickname || !chatClient || !roomId) return;

    if (checkForSpam(messageInput)) {
      console.warn('도배 또는 부정 키워드가 감지되었습니다.');
      Swal.fire({
        icon: 'warning',
        title: '부적절한 메시지 감지',
        text: '부적절한 메시지가 감지되었습니다. 다시 시도해 주세요.',
        confirmButtonText: '확인'
      });
      return;
    }

    const now = Date.now();
    const recentMessages = lastMessages.filter(msg => now - msg.timestamp < 3000);

    if (recentMessages.length >= 2) {
      setIsChatRestricted(true);
      setTimeout(() => setIsChatRestricted(false), 20000); // 20초 제한
      console.warn('3초 이내에 3번의 메시지가 감지되었습니다. 20초 동안 채팅이 제한됩니다.');
      return;
    }

    try {
      const channel = chatClient.realtime.channels.get(roomId);
      await channel.publish('message', { text: messageInput.trim() });
      // 클라이언트 IP 가져오기
      const ip = await fetchClientIp();
      // Supabase에 메시지 저장 (ip 포함)
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          room_id: roomId,
          user_id: clientId,
          content: messageInput.trim(),
          nickname: profileData.nickname,
          avatar_url: profileData.avatar,
          ip: ip,
          created_at: new Date().toISOString(),
        });

      if (error) {
        console.error('메시지 저장 오류:', error);
      }

      setLastMessages([...recentMessages, { timestamp: now }]);
      setMessageInput("");
    } catch (error) {
      console.error('Error sending message via core SDK:', error);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSend();
    }
  };

  // 드롭다운 바깥 클릭 시 닫기
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e: MouseEvent) => {
      setContextMenu(null);
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [contextMenu]);

  if (!chatClient || !isReady) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="h-8 w-8 animate-spin text-orange-500">로딩중...</div>
      </div>
    );
  }

  return (
    <ChatClientProvider client={chatClient}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold">게임 채팅</CardTitle>
          <div className="flex items-center text-sm text-muted-foreground">
            <Users className="w-4 h-4 mr-1" />
            <span>{members.length}명 참여중</span>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-72 mb-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800/50">
            {messages.map((msg, index) => (
              <div
                key={msg.id || index}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, msg });
                }}
                className={`flex items-start gap-2 mb-3 ${msg.clientId === clientId ? 'justify-end' : 'justify-start'}`}
              >
                {msg.clientId !== clientId && (
                  <Avatar className="w-7 h-7 border">
                    <AvatarFallback className="text-xs">{msg.data?.profileData?.nickname?.slice(0, 1) || '?'}</AvatarFallback>
                  </Avatar>
                )}
                <div className={`max-w-[75%] p-2.5 rounded-lg shadow-sm ${msg.clientId === clientId
                  ? 'bg-orange-500 text-white rounded-br-none'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-none'
                }`}>
                  {msg.clientId !== clientId && (
                    <p className="text-xs font-semibold mb-1 text-orange-600 dark:text-orange-400">{ msg.data?.profileData?.nickname }</p>
                  )}
                  <p className="text-sm break-words whitespace-pre-wrap">{(msg.data as any)?.text ?? msg.data ?? ''}</p>
                  {msg.timestamp && <p className="text-xs text-right mt-1 opacity-60">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>}
                </div>
                {msg.clientId === clientId && (
                  <Avatar className="w-7 h-7 border">
                    <AvatarImage src={profileData.avatar || undefined} alt={profileData.nickname || ''} />
                    <AvatarFallback className="text-xs">{profileData.nickname?.slice(0, 1) || '나'}</AvatarFallback>
                  </Avatar>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </ScrollArea>
          <div className="flex items-center gap-2 mt-2">
            <Input
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={profileData.nickname ? "메시지를 입력하세요..." : "채팅 참여를 위해 로그인 또는 페이지 새로고침"}
              className="flex-1"
              disabled={!profileData.nickname}
            />
            <Button onClick={handleSend} disabled={!messageInput.trim() || !profileData.nickname}>
              <Send className="w-4 h-4" />
              <span className="sr-only">보내기</span>
            </Button>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            채팅 작성 시 IP가 기록되며, 악성 채팅 작성 시 사이버 수사대에 의뢰될 수 있습니다.
          </p>
          {contextMenu && (
            <div
              className="fixed z-50 bg-white border rounded shadow-lg w-32 text-sm"
              style={{ top: contextMenu.mouseY, left: contextMenu.mouseX }}
              onClick={e => e.stopPropagation()}
              onContextMenu={e => e.preventDefault()}
            >
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                onClick={e => {
                  e.stopPropagation();
                  handleReport(contextMenu.msg);
                }}
              >신고</button>
              <button
                className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${!(contextMenu.msg.clientId === clientId || (contextMenu.msg.clientId?.startsWith('손님_'))) ? 'text-gray-300 cursor-not-allowed' : ''}`}
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(contextMenu.msg);
                }}
                disabled={!(contextMenu.msg.clientId === clientId || (contextMenu.msg.clientId?.startsWith('손님_')))}
              >삭제</button>
            </div>
          )}
        </CardContent>
      </Card>
    </ChatClientProvider>
  );
};